import posthog from 'posthog-js';

/**
 * Initialize PostHog for product analytics
 */
export function initPostHog() {
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
  const host = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;
  const environment = import.meta.env.VITE_ENVIRONMENT || 'development';

  if (!apiKey || !host) {
    console.warn('PostHog API key or host not configured, skipping initialization');
    return;
  }

  posthog.init(apiKey, {
    api_host: host,
    // Enable debug mode in development to see what's happening
    debug: environment === 'development',
    // Enable autocapture for clicks, form submissions, etc.
    autocapture: true,
    // Capture pageviews automatically
    capture_pageview: true,
    // Capture pageleaves
    capture_pageleave: true,
    // Enable session recording
    session_recording: {
      recordCrossOriginIframes: false,
      maskAllInputs: true,
      maskTextSelector: '[data-posthog-mask]',
    },
    // Enable PostHog - don't opt out in development for testing
    loaded: (posthog) => {
      console.log('✅ PostHog loaded successfully', { environment, api_host: host });
      // Always opt in to allow testing in development
      posthog.opt_in_capturing();
      console.log('✅ PostHog capturing enabled');
    },
    // Persistence options
    persistence: 'localStorage+cookie' as const,
    // Cross-subdomain cookie support
    cross_subdomain_cookie: false,
    // Secure cookie in production
    secure_cookie: environment === 'production',
  });
}

/**
 * Check if PostHog is loaded and ready
 */
function isPostHogLoaded(): boolean {
  return posthog && posthog.__loaded === true;
}

/**
 * Identify a user in PostHog
 */
export function identifyPostHogUser(userId: string, properties?: Record<string, any>) {
  if (isPostHogLoaded()) {
    posthog.identify(userId, properties);
  }
}

/**
 * Reset user identification on logout
 */
export function resetPostHogUser() {
  if (isPostHogLoaded()) {
    posthog.reset();
  }
}

/**
 * Capture a custom event
 */
export function capturePostHogEvent(eventName: string, properties?: Record<string, any>) {
  if (isPostHogLoaded()) {
    posthog.capture(eventName, properties);
  }
}

/**
 * Set user properties
 */
export function setPostHogUserProperties(properties: Record<string, any>) {
  if (isPostHogLoaded()) {
    posthog.setPersonProperties(properties);
  }
}

/**
 * Get PostHog instance for advanced usage
 */
export function getPostHog() {
  return posthog;
}

/**
 * Test function to manually send a test event
 * Usage: testPostHogEvent() or testPostHogEvent('custom event', { custom: 'property' })
 */
export function testPostHogEvent(eventName = 'my event', properties = { property: 'value' }) {
  if (isPostHogLoaded()) {
    posthog.capture(eventName, properties);
    console.log('✅ PostHog test event sent:', { event: eventName, properties });
    return true;
  } else {
    console.warn('⚠️ PostHog not loaded yet. Waiting...');
    // Wait a bit and try again
    setTimeout(() => {
      if (isPostHogLoaded()) {
        posthog.capture(eventName, properties);
        console.log('✅ PostHog test event sent (delayed):', { event: eventName, properties });
      } else {
        console.error('❌ PostHog failed to load');
      }
    }, 1000);
    return false;
  }
}

// Expose PostHog and test function globally for console testing
if (typeof window !== 'undefined') {
  (window as any).posthog = posthog;
  (window as any).testPostHogEvent = testPostHogEvent;
  console.log('🔧 PostHog test functions available: window.posthog.capture() or window.testPostHogEvent()');
}

